export enum EKeyInitiativeGoalMode {
  RunRate = 'run-rate',
  Incremental = 'incremental',
  NoSalesTarget = 'no-sales-target',
}

type TSalesTargetField =
  `${'Goal' | 'Incremental'}${'Ndp' | 'Net' | 'TNet' | 'UTNet'}${'' | 'USD' | 'LocalCurrency'}`

type TPersistedKeyInitiativeGoal = Partial<Record<TSalesTargetField, number | null>> & {
  EntryId?: number | null
  IsFromGlobalPartner?: number | null
  InitType?: string | null
  AddToBridges?: number | null
}

const TARGET_PREFIXES = ['Goal', 'Incremental'] as const
const TARGET_VALUE_TYPES = ['Ndp', 'Net', 'TNet', 'UTNet'] as const
const TARGET_CURRENCIES = ['', 'USD', 'LocalCurrency'] as const

export const getPersistedKeyInitiativeGoalMode = (
  initiative: TPersistedKeyInitiativeGoal
): EKeyInitiativeGoalMode => {
  // AddToBridges controls bridge participation, not the financial goal type.
  const hasIncrementalTarget = TARGET_VALUE_TYPES.some((valueType) =>
    TARGET_CURRENCIES.some((currency) => {
      const field: TSalesTargetField = `Incremental${valueType}${currency}`
      return Number(initiative[field] ?? 0) > 0
    })
  )
  if (hasIncrementalTarget) {
    return EKeyInitiativeGoalMode.Incremental
  }

  const hasNoSalesTarget = TARGET_PREFIXES.every((prefix) =>
    TARGET_VALUE_TYPES.every((valueType) =>
      TARGET_CURRENCIES.every((currency) => {
        const field: TSalesTargetField = `${prefix}${valueType}${currency}`
        return Number(initiative[field] ?? 0) === 0
      })
    )
  )

  return hasNoSalesTarget ? EKeyInitiativeGoalMode.NoSalesTarget : EKeyInitiativeGoalMode.RunRate
}

export const shouldAddToBridgesForGoalMode = (mode: EKeyInitiativeGoalMode): boolean =>
  mode === EKeyInitiativeGoalMode.Incremental

export const isIncrementalForcedByInvestmentSource = (investmentSource?: string): boolean =>
  Boolean(investmentSource)

interface IShouldSetIncrementalForFundingParams {
  previousInvestmentSource?: string
  currentInvestmentSource?: string
  previousIsInvestmentNeeds: boolean
  currentIsInvestmentNeeds: boolean
  isIncrementalModeLocked: boolean
}

export const shouldSetIncrementalForFunding = ({
  previousInvestmentSource,
  currentInvestmentSource,
  previousIsInvestmentNeeds,
  currentIsInvestmentNeeds,
  isIncrementalModeLocked,
}: IShouldSetIncrementalForFundingParams): boolean => {
  if (!currentInvestmentSource) {
    return false
  }

  const isInvestmentSourceChanged = previousInvestmentSource !== currentInvestmentSource
  const isInvestmentNeedsEnabled = !previousIsInvestmentNeeds && currentIsInvestmentNeeds

  return isIncrementalModeLocked || isInvestmentSourceChanged || isInvestmentNeedsEnabled
}
